import { type AutocompleteValue } from '@mui/material';
import { isArray } from 'lodash';
import { useState } from 'react';

import { FilterChipAndPopover } from './components/ChipAndPopover';
import { AutocompleteField } from './components/Fields/Autocomplete';

interface Props<TValue extends string, TMultiple extends boolean | undefined> {
  label: string;
  autoCompleteLabel?: string;
  options: readonly TValue[];
  getOptionLabel?: (option: TValue) => string;
  getSelectedLabel?: (
    value: AutocompleteValue<TValue, TMultiple, false, false> | undefined
  ) => string | undefined;
  value: AutocompleteValue<TValue, TMultiple, false, false> | undefined;
  setValue: (option: AutocompleteValue<TValue, TMultiple, false, false> | null) => void;
  multiple?: TMultiple;
  loading?: boolean;
}

export function AutocompleteFilter<
  TValue extends string,
  TMultiple extends boolean | undefined = false,
>(props: Props<TValue, TMultiple>) {
  const {
    label,
    autoCompleteLabel,
    options,
    value,
    multiple,
    setValue,
    getOptionLabel,
    getSelectedLabel,
    loading,
  } = props;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const getSelectedValue = (
    value: AutocompleteValue<TValue, TMultiple, false, false> | undefined
  ): string | undefined => {
    if (isArray(value)) {
      return getSelectedLabel?.(value) ?? value.join(', ');
    }
    const stringValue = value?.toString();
    if (!stringValue) {
      return undefined;
    }
    return getSelectedLabel?.(value) ?? stringValue;
  };

  return (
    <FilterChipAndPopover
      fieldLabel={label}
      selectedValue={getSelectedValue(value)}
      isOpen={isPopoverOpen}
      setIsOpen={setIsPopoverOpen}
      onDelete={value ? () => setValue(null) : undefined}
    >
      <AutocompleteField
        label={autoCompleteLabel ?? label}
        value={value}
        multiple={multiple}
        onChange={(_, newValue) => {
          setValue(newValue);
          if (newValue) {
            setIsPopoverOpen(false);
          }
        }}
        options={options}
        getOptionLabel={
          getOptionLabel
            ? getOptionLabel
            : (option) =>
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
                String(option)
        }
        loading={loading}
      />
    </FilterChipAndPopover>
  );
}
